import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAPIBaseURL } from '../services/helpers'
import { postData } from '../services/request'
import { TicketIcon } from '@heroicons/react/24/outline'
import Logo from '../asset/images/logo.svg'

const AdminLogin = () => {
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const login = async (e) => {
    e.preventDefault()
    if (!username || !password) {
      setError('Username and password are required')
      return
    }
    setLoading(true)
    setError('')

    try {
      const url = `${getAPIBaseURL()}/v1/admins/login`
      const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
      const form = new URLSearchParams()
      form.append('username', username.trim())
      form.append('password', password)

      const requestResult = await postData(url, headers, form.toString())
      setLoading(false)

      if (requestResult.error === '') {
        if (requestResult.result.status === 200) {
          // Backend returns: { message: 'successful', data: user }
          // Axios wraps it: res.result.data = { message: 'successful', data: user }
          // So user is at: res.result.data.data
          const userData = requestResult.result.data?.data || requestResult.result.data
          
          if (userData) {
            // Store admin data
            localStorage.setItem('admin', JSON.stringify(userData))
            // Also store as user for RequireAuth compatibility
            localStorage.setItem('user', JSON.stringify(userData))
            // Set session flag
            sessionStorage.setItem('active', '1')
            sessionStorage.setItem('admin-active', '1')
            
            // Redirect to admin dashboard
            navigate('/admin', { replace: true })
          } else {
            setError('Login successful but no user data received')
          }
        } else {
          const errorMsg = requestResult.result?.data?.message || requestResult.result?.data?.data || 'Invalid credentials'
          setError(errorMsg)
        }
      } else {
        // Handle error response
        const errorMsg = requestResult.result?.data?.message || requestResult.result?.data?.data || 'Login failed'
        setError(errorMsg)
      }
    } catch (err) {
      setLoading(false)
      console.error('Admin login error:', err)
      setError('An unexpected error occurred. Please try again.')
    }
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-slate-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center items-center gap-1.5 mb-6 group">
          <img
            src={Logo}
            alt="Obule"
            className="h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-110"
          />
          <span className="text-3xl font-semibold tracking-wide text-slate-900">
            bule
          </span>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-slate-900">
          Admin Portal
        </h2>
        <p className="mt-2 text-center text-sm text-slate-500">
          Sign in to your admin account
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white px-4 py-8 shadow-xl border border-slate-100 sm:rounded-2xl sm:px-10">
          <form className="space-y-6" onSubmit={login}>
            {error && (
              <div className="rounded-md bg-red-50 p-4 border border-red-100">
                <div className="flex">
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800">Login failed</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setError('')}
                    className="ml-auto -mx-1.5 -my-1.5 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600"
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium leading-6 text-slate-900">
                Username
              </label>
              <div className="mt-2">
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="block w-full rounded-md border-0 py-1.5 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm sm:leading-6 transition-shadow"
                  placeholder="Enter your username"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium leading-6 text-slate-900">
                Password
              </label>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-md border-0 py-1.5 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm sm:leading-6 transition-shadow"
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !username.trim() || !password}
                className="flex w-full justify-center rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin
